import {useEventBus} from "../../../assets-dev/ts/modules/use-event-bus";

export class ComponentSlider {
    private readonly slideClass = '.hd-slide';
    private readonly activeClass = 'active';
    private readonly animationSlideClass = 'hd-animation';
    private readonly animateSlideClass = 'hd-animate';
    private readonly dotClass = '.hd-slider__dot';
    private readonly arrowLeftClass = '.hd-slider__slide-change-left';
    private readonly arrowRightClass = '.hd-slider__slide-change-right';

    private readonly defaultInterval = 3000;
    private readonly defaultAutoplay = true;
    private readonly pauseOnHover = true;

    private el: HTMLElement;
    private slides: HTMLElement[];
    private dots: HTMLElement[];
    private arrowLeft: HTMLElement | null;
    private arrowRight: HTMLElement | null;
    private activeSlideIndex: number;
    private autoplay: boolean;
    private interval: number;

    private intervalId;
    private animateSlideTimeout;
    private mouseEnterFn;
    private mouseLeaveFn;
    private arrowLeftFn;
    private arrowRightFn;
    private dotsClickFns: any[] = [];

    constructor(el: HTMLElement) {
        this.el = el;
        this.slides = <HTMLElement[]>Array.from(el.querySelectorAll(this.slideClass));
        this.dots = <HTMLElement[]>Array.from(el.querySelectorAll(this.dotClass));
        this.arrowLeft = el.querySelector(this.arrowLeftClass);
        this.arrowRight = el.querySelector(this.arrowRightClass);
        const activeSlide = this.slides.find(slide => slide.classList.contains(this.activeClass)) || this.slides[0];
        this.activeSlideIndex = Number(activeSlide.dataset.index) || 0;
        this.autoplay =  false && (this.el.dataset.hasOwnProperty('autoplay') ? this.el.dataset.autoplay == 'true' : this.defaultAutoplay);
        this.interval = Math.max(100, Number(this.el.dataset.interval)) || this.defaultInterval;
    }

    public onInit() {
        this.initDots();
        this.initArrows();
        this.setActiveSlide(this.activeSlideIndex);
        this.initAutoplay();

        if (this.pauseOnHover) {
            this.initPauseOnHover();
        }
    }

    public onDestroy() {
        this.pauseAutoplay();
        this.el.removeEventListener('mouseenter', this.mouseEnterFn);
        this.el.removeEventListener('mouseleave', this.mouseLeaveFn);
        this.destroyArrows();
        this.dots.forEach((dot, index) => {
            if (this.dotsClickFns[index]) {
                dot.removeEventListener('click', this.dotsClickFns[index]);
            }
        });
        this.dotsClickFns.forEach((e) => {
            e.el.removeEventListener(e.event, e.fn);
        });
        this.dotsClickFns = [];
    }


    private initAutoplay() {
        this.pauseAutoplay();
        if (this.autoplay) {
            this.intervalId = setInterval(() => {
                this.setActiveSlide(this.activeSlideIndex + 1);
            }, this.interval);
        }
    }

    private pauseAutoplay() {
        clearInterval(this.intervalId);
    }

    private initPauseOnHover() {
        this.mouseEnterFn = this.pauseAutoplay.bind(this);
        this.mouseLeaveFn = this.initAutoplay.bind(this);
        this.el.addEventListener('mouseenter', this.mouseEnterFn);
        this.el.addEventListener('mouseleave', this.mouseLeaveFn);
    }

    private initDots() {
        this.dots.forEach((el, index) => {
            const slideIndex = el.dataset.index != null ? Number(el.dataset.index) : index;
            const fn = this.setActiveSlide.bind(this, slideIndex);
            const event = 'click';
            const e = {
                el,
                event,
                fn,
            };
            this.dotsClickFns.push(e);
            el.addEventListener(event, fn);
        });
    }

    private initArrows() {
        if (this.arrowLeft) {
            this.arrowLeftFn = this.setSlideDelta.bind(this, -1);
            this.arrowLeft.addEventListener('click', this.arrowLeftFn);
        }
        if (this.arrowRight) {
            this.arrowRightFn = this.setSlideDelta.bind(this, 1);
            this.arrowRight.addEventListener('click', this.arrowRightFn);
        }
    }

    private destroyArrows() {
        if (this.arrowLeft && this.arrowLeftFn) {
            this.arrowLeft.removeEventListener('click', this.arrowLeftFn);
        }
        if (this.arrowRight && this.arrowRightFn) {
            this.arrowRight.removeEventListener('click', this.arrowRightFn);
        }
    }

    private setSlideDelta(delta: number) {
        const newIndex = this.activeSlideIndex + delta;
        this.setActiveSlide(newIndex);
    }

    private setActiveSlide(newIndex: number) {
        const change = newIndex % this.slides.length;
        const positiveIndex = (this.slides.length + change) % this.slides.length;
        this.activeSlideIndex = positiveIndex;
        this.slides.forEach(el => this.toggleActiveSlide(el));
        this.dots.forEach(el => this.toggleActiveDot(el));
    }

    private toggleActiveSlide(el) {
        const animationEl = el.querySelector(`.${this.animationSlideClass}`);
        if (Number(el.dataset.index) !== this.activeSlideIndex) {
            el.classList.remove(this.activeClass);
            if (animationEl) {
                animationEl.classList.remove(this.animateSlideClass);
            }
        } else {
            el.classList.add(this.activeClass);
            clearTimeout(this.animateSlideTimeout);
            this.animateSlideTimeout = setTimeout(() => {
                if (animationEl) {
                    animationEl.classList.add(this.animateSlideClass);
                }
            }, 100);
        }
    }
    private toggleActiveDot(el) {
        if (Number(el.dataset.index) !== this.activeSlideIndex) {
            el.classList.remove(this.activeClass);
        } else {
            el.classList.add(this.activeClass);
        }
    }
}

export function useComponentSlider() {
    useEventBus('hd-slider', ComponentSlider);
}